import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Routes that must NOT be indexed: auth flows, user-private areas, admin, and
// cart/checkout. These are the SPA equivalents of the pages Google was flagging
// as thin/duplicate. We emit a noindex robots tag for them and still set a
// self-referencing canonical so any stray param variants collapse to one URL.
const NOINDEX_PREFIXES = [
  '/login',
  '/signin',
  '/register',
  '/create-account',
  '/registration-success',
  '/verify-email',
  '/verify-additional-email',
  '/forgot-password',
  '/reset-password',
  '/profile',
  '/my-blogs',
  '/my-services',
  '/cart',
  '/messages',
  '/admin',
  '/blog/create',
  '/blog/edit',
];

const CANONICAL_ORIGIN = 'https://unsupervisedbias.com';
const SITE_NAME = 'UnsupervisedBias';
const DEFAULT_TITLE = 'UnsupervisedBias - AI & Data Science Solutions';
const DEFAULT_DESCRIPTION = 'UnsupervisedBias - AI & Data Science Solutions';

/**
 * Per-route <title> and <meta name="description">.
 *
 * This is a Create React App SPA: nginx serves ONE index.html for every URL, so
 * before this table existed every page on the site shipped the same title and
 * the same description -- "UnsupervisedBias - AI & Data Science Solutions" --
 * and Google's pre-JS pass saw a thousand byte-identical documents with nothing
 * to tell them apart. That is the mechanism behind the 136 URLs in Search
 * Console's "Duplicate without user-selected canonical" (2026-09-02): a
 * self-referencing canonical alone cannot fix it, because the pages still look
 * identical once Google follows the canonical.
 *
 * Keys are pathnames with no trailing slash, matched exactly first and then by
 * longest prefix, so '/trading/positions' beats '/trading'.
 *
 * Pages that know something this table cannot -- RecessionMonitor and
 * TrendArticle -- set their own document.title in their own effects, which run
 * after this one and therefore win. The entries here are their fallback for the
 * window before their data loads.
 */
const ROUTE_META = {
  '/': [
    'UnsupervisedBias - Modern Web Solutions, Powered by Data',
    'Custom websites, data pipelines and AI chatbots for small businesses. See our work, read what customers say, and book a discovery call.',
  ],
  '/about': [
    `About ${SITE_NAME} - Who We Are and What We Value`,
    'The people behind UnsupervisedBias, the core values we work to, and how we approach web, data and AI projects.',
  ],
  '/contact': [
    `Contact ${SITE_NAME}`,
    'Get in touch with UnsupervisedBias - book a discovery call, send us a message, or find ways to support the project.',
  ],
  '/faq': [
    `Frequently Asked Questions | ${SITE_NAME}`,
    'Answers about our custom AI chatbots - how they work, demoing one on your own files, embedding a chat bubble on your site, and how uploads are kept private.',
  ],
  '/apps': [
    `Our Apps | ${SITE_NAME}`,
    'Mobile and web apps built by UnsupervisedBias, including what you can download today and what is still coming.',
  ],
  '/authors': [
    `Meet Our Writers | ${SITE_NAME}`,
    'The writers and analysts behind UnsupervisedBias articles and trend reports.',
  ],
  '/camels': [
    `Camels Hockey Club - Photos & Media | ${SITE_NAME}`,
    'Photo and media gallery for the Camels Hockey Club.',
  ],
  '/wharf': [
    `District Wharf Hockey Tournament - Photos & Stickers | ${SITE_NAME}`,
    'Photos and stickers from the District Wharf hockey tournament in Washington, DC.',
  ],
  '/book-call': [
    `Book a Discovery Call | ${SITE_NAME}`,
    'Book a call to talk through automations for your business, and see what to expect from working with UnsupervisedBias.',
  ],
  '/2026RecessionMonitoring': [
    `2026 Recession Monitor | ${SITE_NAME}`,
    'A live dashboard of 2026 recession indicators, refreshed automatically from public economic data.',
  ],
  '/trends': [
    `Trend Reports | ${SITE_NAME}`,
    'Data-driven trend reports from UnsupervisedBias.',
  ],
  '/terms-of-service': [
    `Terms of Service | ${SITE_NAME}`,
    'The terms governing use of UnsupervisedBias websites and services, including who we are, eligibility and acceptable use.',
  ],
  '/privacy-policy': [
    `Privacy Policy | ${SITE_NAME}`,
    'What information UnsupervisedBias collects, how we use it, and the choices you have over your data.',
  ],
  '/data-deletion': [
    `Data Deletion Request | ${SITE_NAME}`,
    'What data UnsupervisedBias holds about you and how to request that we delete it.',
  ],
  '/dmca': [
    `DMCA & Copyright Policy | ${SITE_NAME}`,
    'How to report copyright infringement to UnsupervisedBias and how we handle takedown notices.',
  ],
  // Trading dashboards render live account data. They are given distinct titles
  // so they do not read as duplicates of each other, but they are deliberately
  // left out of sitemap.xml -- a dashboard is not a landing page.
  '/trading': [`Trading Dashboard | ${SITE_NAME}`, 'Live trading dashboard.'],
  '/trading/positions': [`Positions | ${SITE_NAME}`, 'Open trading positions.'],
  '/trading/orders': [`Orders | ${SITE_NAME}`, 'Trading order history.'],
  // noindex routes still get distinct titles: the robots tag only lands after
  // Googlebot renders the JS, so the title is what a non-rendering pass sees.
  '/login': [`Sign In | ${SITE_NAME}`, 'Sign in to your UnsupervisedBias account.'],
  '/signin': [`Sign In | ${SITE_NAME}`, 'Sign in to your UnsupervisedBias account.'],
  '/register': [`Create an Account | ${SITE_NAME}`, 'Create an UnsupervisedBias account.'],
  '/create-account': [`Create an Account | ${SITE_NAME}`, 'Create an UnsupervisedBias account.'],
  '/forgot-password': [`Reset Your Password | ${SITE_NAME}`, 'Request a password reset link.'],
  '/reset-password': [`Set a New Password | ${SITE_NAME}`, 'Choose a new password for your account.'],
  '/verify-email': [`Verify Your Email | ${SITE_NAME}`, 'Confirm your email address.'],
  '/profile': [`Your Profile | ${SITE_NAME}`, 'Manage your UnsupervisedBias profile.'],
  '/messages': [`Messages | ${SITE_NAME}`, 'Your UnsupervisedBias messages.'],
  '/cart': [`Cart | ${SITE_NAME}`, 'Your UnsupervisedBias cart.'],
};

// Longest-prefix fallback so '/verify-email/abc123' and '/trading/dashboard'
// inherit from their parent entry instead of dropping to the site default.
const ROUTE_META_KEYS = Object.keys(ROUTE_META).sort((a, b) => b.length - a.length);

function metaForPath(path) {
  if (ROUTE_META[path]) return ROUTE_META[path];
  const key = ROUTE_META_KEYS.find((k) => k !== '/' && path.startsWith(`${k}/`));
  return key ? ROUTE_META[key] : [DEFAULT_TITLE, DEFAULT_DESCRIPTION];
}

function upsertLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  return el;
}

// `attr` is 'name' for standard meta tags and 'property' for Open Graph.
function upsertMeta(attr, key, content) {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  return el;
}

function upsertRobots(content) {
  let el = document.querySelector('meta[name="robots"]');
  if (content == null) {
    if (el) el.parentNode.removeChild(el);
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', 'robots');
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/**
 * RouteSEO
 *
 * Runs inside <Router> and, on every navigation, sets:
 *   1. A self-referencing <link rel="canonical"> built from origin + pathname
 *      only (query string and hash stripped). This is the fix for the 153
 *      "Duplicate without user-selected canonical" pages: every route now
 *      declares its own canonical URL, so param variants
 *      (?redirect=, ?ref=, utm_*, trailing junk) all collapse to one.
 *   2. A per-route <title>, <meta name="description"> and matching og:* tags,
 *      from ROUTE_META above. Without these every URL served the same two
 *      strings and looked like a duplicate of every other URL even after
 *      Google honoured the canonical.
 *   3. A robots noindex,follow tag on auth/admin/user-private routes so Google
 *      stops treating them as thin duplicates. Content routes get the tag
 *      removed (indexable).
 *
 * Individual content pages (BlogPost, TrendArticle, RecessionMonitor) may
 * override the canonical or title afterwards with a more specific value;
 * because those run in their own effects after this one, their value wins.
 */
export default function RouteSEO() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Normalize: strip trailing slash (except root) so "/about/" and "/about"
    // share one canonical.
    let path = pathname || '/';
    if (path.length > 1 && path.endsWith('/')) path = path.slice(0, -1);

    const url = `${CANONICAL_ORIGIN}${path}`;
    upsertLink('canonical', url);

    const [title, description] = metaForPath(path);
    document.title = title;
    upsertMeta('name', 'description', description);
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('property', 'og:type', path === '/' ? 'website' : 'article');

    const isNoindex = NOINDEX_PREFIXES.some(
      (p) => path === p || path.startsWith(`${p}/`)
    );
    upsertRobots(isNoindex ? 'noindex, follow' : 'index, follow');
  }, [pathname]);

  return null;
}
